import { ChargerAttributes, createEmptyCharger } from '../models/charger-model';
import { ApiRequest } from '../services/api-requests';
import {
  addDemoCharger,
  DEMO_USER_ID,
  findDemoCharger,
  getDemoChargers,
  getDemoChargersWithMonthlyRevenue,
  getDemoHistory,
  getDemoPartnerChargerTransactionDetails,
  getDemoPartnerChargers,
  getDemoPartnerTransactionDetails,
} from './demo-fixtures';

// Result returned by an intercepted route. `payload` is the unwrapped value
// the corresponding service would return on success. If `handled` is false
// the caller falls through to the real network.
export interface DemoApiResult {
  handled: boolean;
  status?: number;
  payload?: unknown;
}

const HANDLED_NOT_FOUND: DemoApiResult = {
  handled: true,
  status: 404,
  payload: { message: 'demo: not found' },
};

const ok = <T>(payload: T): DemoApiResult => ({
  handled: true,
  status: 200,
  payload,
});

const matchSingleChargerRoute = (route: string): string | null => {
  // `charger/<id>/manufacturerInfo/<manufacturer>` — handled separately
  if (route.includes('/manufacturerInfo/')) return null;
  // `charger/<id>/qrcode`, `charger/<id>/qrcode/email` — let real backend
  // attempt these (they'll fail silently in demo, but the demo flow doesn't
  // hinge on QR codes).
  return null;
};

export const tryDemoIntercept = (req: ApiRequest): DemoApiResult => {
  const route = req.route ?? '';
  const method = req.request?.method ?? 'GET';

  // Host's own chargers
  if (route === 'charger/getMyChargers' && method === 'GET') {
    return ok(getDemoChargersWithMonthlyRevenue());
  }

  // Public list — used by the map/search; demo shows the host's chargers
  // there too so the curbside fault can be demoed end-to-end.
  if (route === 'charger/getAll' && method === 'GET') {
    return ok(getDemoChargers());
  }

  // Single-charger lookup (POST charger/getSingleCharger { chargerId })
  if (route === 'charger/getSingleCharger' && method === 'POST') {
    const id = (req.request?.body as { chargerId?: string } | undefined)
      ?.chargerId;
    const match = id ? findDemoCharger(id) : undefined;
    if (match) return ok([match]);
    // Unknown id falls through so a real charger can still be demoed if needed
    return { handled: false };
  }

  // Host's charging history
  if (route === 'charger/getMyChargerHistories' && method === 'GET') {
    return ok(getDemoHistory());
  }

  // Driver charging history (used by TransactionHistory screen)
  if (route === 'charger/chargerHistoryByUser' && method === 'POST') {
    return ok(getDemoHistory());
  }

  // Manufacturer lookup for charger validation step
  if (
    method === 'GET' &&
    /^charger\/[^/]+\/manufacturerInfo\/[^/]+$/.test(route)
  ) {
    const [, serial, , manufacturer] = route.split('/');
    return ok({
      name: `Demo ${manufacturer} ${serial}`,
      charge_rate_amps: 40,
      vehicle_connected: false,
      vehicle_charging: false,
      charger_on: true,
      manufacturer,
      connected: true,
      connected_changed_time: new Date().toISOString(),
      evse_gun_type: 'J1772',
    });
  }

  // Charger registration (POST charger)
  if (route === 'charger' && method === 'POST') {
    const body = (req.request?.body ?? {}) as Partial<ChargerAttributes> & {
      manufacturer_name?: string;
      charger_model?: string;
      cost_kwh_peak?: number;
      cost_kwh_off_peak?: number;
      price_kwh_peak?: number;
      price_kwh_off_peak?: number;
    };
    const uuid = `demo-charger-${Date.now()}`;
    const created = createEmptyCharger({
      ...body,
      uuid,
      registered_user: DEMO_USER_ID,
      connected: true,
      is_enabled: true,
      can_schedule: true,
      ocpp_connector_status: 'Available',
      financials: {
        price_kwh_peak: Number(body.price_kwh_peak ?? 0.45),
        price_kwh_off_peak: Number(body.price_kwh_off_peak ?? 0.28),
      },
    });
    addDemoCharger(created);
    return ok(created);
  }

  // Supported-chargers manufacturer dropdown (used by ChargerValidation)
  if (route === 'supported-chargers/manufacturers' && method === 'GET') {
    return ok({ success: true, count: 2, data: ['Emporia', 'Virtual'] });
  }

  // Active charges list (driver-side) — demo has none
  if (route === 'user/getActiveCharges' && method === 'GET') {
    return ok([]);
  }

  // Partner portal — Meander Partner Chargers with backfilled activity
  if (route === 'charger/partner/getPartnerChargers' && method === 'GET') {
    return ok({ chargers: getDemoPartnerChargers() });
  }

  if (
    route === 'charger/partner/getAllPartnerChargerTransactionDetails' &&
    method === 'POST'
  ) {
    const body = (req.request?.body ?? {}) as {
      startDate?: string;
      endDate?: string;
    };
    return ok(getDemoPartnerTransactionDetails(body));
  }

  if (
    route === 'charger/partner/getChargerTransactionDetails' &&
    method === 'POST'
  ) {
    const body = (req.request?.body ?? {}) as {
      chargerId?: string;
      startDate?: string;
      endDate?: string;
    };
    const details = body.chargerId
      ? getDemoPartnerChargerTransactionDetails(body.chargerId, body)
      : null;
    if (details) return ok(details);
    return HANDLED_NOT_FOUND;
  }

  // Single-charger pattern (fall-through hook, currently unused)
  if (matchSingleChargerRoute(route)) {
    return HANDLED_NOT_FOUND;
  }

  return { handled: false };
};
